import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  saveTokens
} from '../utils'
import { IApiResponse, LoginResponse } from './types'

const baseUrl = process.env.REACT_APP_BASE_URL ?? ''

// Re-export so older callers keep working.
export { getAccessToken } from '../utils'

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: { 'Content-Type': 'application/json' }
})

// ---------- request: attach Bearer ----------

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken()
    if (config.headers.Authorization !== false && token) {
      ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`)
    } else {
      delete config.headers.Authorization
    }
    // The instance default is `Content-Type: application/json`. When the caller
    // sends a FormData body axios's built-in serialiser would otherwise still
    // see `application/json` and try to JSON-stringify it, which silently
    // collapses every FormData entry to `{}`. Strip the header so axios falls
    // through to its FormData branch and sets the correct boundary itself.
    if (
      typeof FormData !== 'undefined' &&
      config.data instanceof FormData
    ) {
      ;(config.headers as AxiosHeaders).delete('Content-Type')
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

// ---------- response: single-flight refresh on 401 ----------
//
// Strategy: the first 401 triggers a /auth/refresh call; concurrent 401s are
// queued on the same promise. On success we replay each queued request with
// the new access token. On failure we clear tokens and bounce to /login.

let refreshing: Promise<string | null> | null = null

const callRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null
  try {
    const resp = await axios.post<IApiResponse<LoginResponse>>(
      `${baseUrl}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    const data = resp.data?.data
    if (!data?.accessToken) return null
    saveTokens(data.accessToken, data.refreshToken ?? '')
    return data.accessToken
  } catch {
    return null
  }
}

const beginRefresh = (): Promise<string | null> => {
  if (!refreshing) {
    refreshing = callRefresh().finally(() => {
      refreshing = null
    })
  }
  return refreshing
}

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean }

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status
    const original = error.config as RetryConfig | undefined

    // Don't try to refresh on the refresh / login / register endpoints themselves.
    //
    // 2FA enrolment is in this list because it legitimately 401s during normal
    // use — a wrong TOTP code is `AUTH_2FA_INVALID` (401), and the caller has
    // no session yet, so refreshing is impossible. Without the exemption the
    // block below would clearTokens() and hard-redirect to /login on every
    // mistyped digit, destroying the in-memory enrolment token.
    const url = original?.url ?? ''
    const isAuthCall =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/2fa/enrollment/') ||
      url.includes('/public/auth/register')

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true
      const newToken = await beginRefresh()
      if (newToken) {
        ;(original.headers as AxiosHeaders).set(
          'Authorization',
          `Bearer ${newToken}`
        )
        return axiosInstance(original as AxiosRequestConfig)
      }
      clearTokens()
      // Use a hard redirect rather than `redirect()` so the SPA fully reloads
      // (clears in-memory React Query / RTK Query caches).
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }

    if (status === 403) {
      // Forbidden — user is authenticated but lacks the role/permission.
      // Don't clear tokens; let the caller surface the error.
    }
    throw error
  }
)

export default axiosInstance
